import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { useYardStores } from '@yard/AppProvider'
import { OTHER_REST_VALUE, paletteColorFor } from '@yard/utils/color-scheme-palette'
import {
  BORDER_DEFAULT,
  BORDER_RULE,
  BORDER_RULE_HOVERED,
  BORDER_WIDTH_DEFAULT,
  BORDER_WIDTH_HIGHLIGHTED,
  FILTERED_OVERLAY,
  OCCUPANCY_EMPTY,
  OCCUPANCY_FULL,
  OCCUPANCY_PARTIAL,
} from '@yard/utils/occupancies.utils'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { useContainerDetailsDrawerWidth } from '../../hooks/use-container-details-drawer-width'
import { ColorScheme, resolveSchemeDisplayName, YardViewStore } from '../../stores/yard.view-store'

interface LegendItemProps {
  swatch: React.ReactNode
  label: string
}

const LegendItem = ({ swatch, label }: LegendItemProps) => (
  <Stack direction='row' alignItems='center' gap={1}>
    {swatch}
    <Typography variant='body2' whiteSpace='nowrap'>
      {label}
    </Typography>
  </Stack>
)

const SwatchBox = ({ sx }: { sx?: object }) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      flexShrink: 0,
      border: `${BORDER_WIDTH_DEFAULT} solid ${BORDER_DEFAULT}`,
      ...sx,
    }}
  />
)

interface Props {
  viewStore: YardViewStore
  viewportWidth: number
}

const SCHEME_LABEL_KEYS: Record<ColorScheme, { key: string; fallback: string }> = {
  portOfDischarge: { key: 'colourByPortOfDischarge', fallback: 'Port of Discharge' },
  outboundCarrierType: { key: 'colourByOutboundCarrier', fallback: 'Outbound Carrier' },
  inboundCarrierType: { key: 'colourByInboundCarrier', fallback: 'Inbound Carrier' },
  operator: { key: 'colourByOperator', fallback: 'Operator' },
}

export const YardBlockLegend = observer(({ viewStore, viewportWidth }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { companyStore } = useYardStores()
  const [expanded, setExpanded] = useState(true)
  const drawerWidth = useContainerDetailsDrawerWidth(viewStore, viewportWidth)
  const isDrawerOpen = viewStore.isContainerDetailsOpen && viewStore.isViewMode
  const rightOffset = isDrawerOpen
    ? `calc(${drawerWidth}px + ${theme.customSpacing.m})`
    : theme.customSpacing.m

  const activeScheme = viewStore.colorScheme
  // Render from the effective set so a stale Rest tick (yard distinct dropped
  // to <= top-N) doesn't show up as "Rest 0" in the legend.
  const selectedValues = viewStore.effectiveSelectedValuesForActiveScheme
  const showSchemeSection = activeScheme !== undefined && selectedValues.length > 0

  const restCount = useMemo(() => {
    if (!activeScheme) return 0
    const restRow = viewStore.topRowsForActiveScheme.find(r => r.isRest)
    return restRow?.count ?? 0
  }, [activeScheme, viewStore.topRowsForActiveScheme])

  const schemeRows = useMemo(() => {
    if (!showSchemeSection || !activeScheme) return []
    const presentMap = new Map(
      viewStore.presentValuesByScheme[activeScheme].map(r => [r.value, r.count]),
    )
    // Resolve the swatch colour here so the registry assignment happens in a
    // single place per row, and so the JSX below doesn't need its own
    // narrowing of activeScheme — TypeScript can't carry the narrowing
    // through the `showSchemeSection` boolean and into the .map callback.
    const rows = selectedValues.map(value => {
      if (value === OTHER_REST_VALUE) {
        return {
          value,
          label: t('colourByRest', 'Rest'),
          count: restCount,
          color: paletteColorFor(activeScheme, value),
        }
      }
      return {
        value,
        label: resolveSchemeDisplayName(activeScheme, value, companyStore),
        count: presentMap.get(value) ?? 0,
        color: paletteColorFor(activeScheme, value),
      }
    })
    // Named rows by count desc; Rest pinned to the bottom regardless of its
    // count so the planner always finds the "everything else" bucket in the
    // same place.
    const named = rows.filter(r => r.value !== OTHER_REST_VALUE).sort((a, b) => b.count - a.count)
    const rest = rows.find(r => r.value === OTHER_REST_VALUE)
    return rest ? [...named, rest] : named
  }, [
    showSchemeSection,
    activeScheme,
    selectedValues,
    viewStore.presentValuesByScheme,
    companyStore,
    restCount,
    t,
  ])

  const schemeHeading =
    activeScheme !== undefined
      ? t('colourByLegendHeading', 'Colour by {attr}', {
          attr: t(SCHEME_LABEL_KEYS[activeScheme].key, SCHEME_LABEL_KEYS[activeScheme].fallback),
        })
      : ''

  return (
    <Card
      elevation={4}
      sx={{
        position: 'absolute',
        bottom: theme.customSpacing.m,
        right: rightOffset,
        zIndex: theme.zIndex.appBar,
        minWidth: 180,
      }}
    >
      <CardHeader
        title={<Typography variant='subtitle2'>{t('legend', 'Legend')}</Typography>}
        action={
          <IconButton size='small' onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        }
        sx={{ pb: 0, pt: 1, px: 2 }}
      />
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 1, px: 2, pb: '12px !important' }}>
          <Stack spacing={1}>
            {showSchemeSection && (
              <>
                <Typography variant='subtitle2'>{schemeHeading}</Typography>
                {schemeRows.map(({ value, label, count, color }) => (
                  <Tooltip key={value} title={label} placement='left'>
                    <Stack direction='row' alignItems='center' gap={1}>
                      <SwatchBox sx={{ backgroundColor: color }} />
                      <Typography
                        variant='body2'
                        whiteSpace='nowrap'
                        overflow='hidden'
                        textOverflow='ellipsis'
                        sx={{ minWidth: 0, flex: 1, maxWidth: 140 }}
                      >
                        {label}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {count}
                      </Typography>
                    </Stack>
                  </Tooltip>
                ))}
                <Divider />
              </>
            )}
            <LegendItem
              swatch={
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    border: `${BORDER_WIDTH_DEFAULT} solid ${BORDER_DEFAULT}`,
                    display: 'flex',
                  }}
                >
                  <Box sx={{ flex: 1, backgroundColor: OCCUPANCY_EMPTY }} />
                  <Box sx={{ flex: 1, backgroundColor: OCCUPANCY_PARTIAL }} />
                  <Box sx={{ flex: 1, backgroundColor: OCCUPANCY_FULL }} />
                </Box>
              }
              label={t('occupancy', 'Occupancy')}
            />
            <LegendItem
              swatch={
                <SwatchBox
                  sx={{
                    backgroundColor: OCCUPANCY_EMPTY,
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: FILTERED_OVERLAY,
                    },
                  }}
                />
              }
              label={t('filteredContainers', 'Filtered containers')}
            />
            <LegendItem
              swatch={
                <SwatchBox
                  sx={{
                    border: `${BORDER_WIDTH_HIGHLIGHTED} solid ${BORDER_RULE}`,
                    backgroundColor: 'transparent',
                  }}
                />
              }
              label={t('allocated', 'Allocated')}
            />
            <LegendItem
              swatch={
                <SwatchBox
                  sx={{
                    border: `${BORDER_WIDTH_HIGHLIGHTED} solid ${BORDER_RULE_HOVERED}`,
                    backgroundColor: 'transparent',
                  }}
                />
              }
              label={t('selectedAllocation', 'Selected allocation')}
            />
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  )
})
